import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { guestWL } from "@/lib/guestWatchlists";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, obj=authed

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: if returning from Google OAuth, skip /me — AuthCallback exchanges the session first.
    if (window.location.hash?.includes("session_id=")) { return; }
    refresh();
  }, [refresh]);

  const loginWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/feed";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const completeGoogleSession = useCallback(async (sessionId) => {
    const guest = guestWL.hasData() ? guestWL.snapshot() : null;
    const { data } = await api.post("/auth/session", null, { headers: { "X-Session-ID": sessionId } });
    setUser(data);
    if (guest) {
      try { await api.post("/watchlists/import", guest); guestWL.clear(); }
      catch { /* best-effort guest migration */ }
    }
    return data;
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (email, password, name) => {
    // Capture any device-saved (guest) watchlists so we can transfer them into the new account.
    const guest = guestWL.hasData() ? guestWL.snapshot() : null;
    const { data } = await api.post("/auth/register", { email, password, name });
    setUser(data);
    if (guest) {
      try {
        await api.post("/watchlists/import", guest);
        guestWL.clear();
      } catch {
        /* migration is best-effort; guest lists remain on device if it fails */
      }
    }
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore network error; session cleared client-side below */ }
    setUser(false);
  }, []);

  const setTier = useCallback(async (tier) => {
    await api.post(`/me/tier?tier=${tier}`);
    await refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ user, login, register, logout, refresh, setTier, setUser, loginWithGoogle, completeGoogleSession }),
    [user, login, register, logout, refresh, setTier, loginWithGoogle, completeGoogleSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
